import { sortBy } from 'lodash';
import { customRandom } from 'nanoid';

export class Determiner {
  patternLowercaseAlphabet = "abcdefghijklmnopqrstuvwxyz"
  patternUppercaseAlphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
  patternAlphabet = [this.patternLowercaseAlphabet, this.patternUppercaseAlphabet]
  patternNumber = "0123456789"
  patternLowercaseAlphabetNumber = [this.patternLowercaseAlphabet, this.patternNumber]
  patternUppercaseAlphabetNumber = [this.patternUppercaseAlphabet, this.patternNumber]
  patternAlphabetNumber = [...this.patternAlphabet, this.patternNumber]

  patternNonSimilarLowercaseAlphabet = "abcdefghjkmnpqrstuvwxyz"
  patternNonSimilarUppercaseAlphabet = "ABCDEFGHJKMNPQRSTUVWXYZ"
  patternNonSimilarAlphabet = [this.patternNonSimilarLowercaseAlphabet, this.patternNonSimilarUppercaseAlphabet]
  patternNonSimilarNumber = "23456789"
  patternNonSimilarLowercaseAlphabetNumber = [this.patternNonSimilarLowercaseAlphabet, this.patternNonSimilarNumber]
  patternNonSimilarUppercaseAlphabetNumber = [this.patternNonSimilarUppercaseAlphabet, this.patternNonSimilarNumber]
  patternNonSimilarAlphabetNumber = [...this.patternNonSimilarAlphabet, this.patternNonSimilarNumber]

  patternSymbol = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~ "
  patternProgrammerSymbol = "!%^*-_=+;:~|."

  constructor(public source: (len: number) => Uint8Array) {
  }

  bytes(len: number) {
    return this.source(len);
  }

  readonly maxUInt32 = 4294967296
  uint32(): number {
    const dataView = new DataView(this.source(4).buffer);
    return dataView.getUint32(0);
  }

  readonly maxIntegerIter = 100

  private rejectionSampling(range: number) {
    let sample: number
    let i = 0
    do {
      sample = this.uint32()
      if (i >= this.maxIntegerIter) {
        break /* just returns biased sample using remainder */
      }
      i++
    } while (!(sample < Math.floor(this.maxUInt32 / range) * range))

    const unsafeCoerced = sample % range
    return unsafeCoerced
  }

  /** [0, range) */
  private integerLessThan(range: number) {
    return this.rejectionSampling(Math.ceil(range))
  }

  /** [min, max] */
  integer(min: number = 0, max: number = 65536): number {
    if (min <= max) {
      const l = Math.ceil(min)
      const h = Math.floor(max)
      return (l + this.integerLessThan(h - l + 1))
    }
    return NaN;
  }

  pattern(len: number, pattern: string | string[] = this.patternLowercaseAlphabetNumber): string {
    const random = this.source.bind(this);

    if (Array.isArray(pattern)) {
      if (len < pattern.length) {
        return customRandom(pattern.join(""), len, random)()
      } else {
        let result = "";
        for (const subpattern of pattern) {
          result += customRandom(subpattern, 1, random)();
        }
        result += customRandom(pattern.join(""), len - pattern.length, random)();
        result = sortBy(result.split(""), () => this.uint32()).join("");
        return result;
      }
    }
    return customRandom(pattern, len, random)()
  }

}

export function MathRandomSource(len: number): Uint8Array {
  const buffer = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    buffer[i] = Math.floor(Math.random() * 0x100)
  }
  return buffer
}

export const MathRandomDeterminer = new Determiner(MathRandomSource)

export function WebCryptoRandomSource(len: number): Uint8Array {
  const buffer = new Uint8Array(len)
  window.crypto.getRandomValues(buffer)
  return buffer;
}

export const WebCryptoDeterminer = new Determiner(WebCryptoRandomSource)

export const BestRandomSource = WebCryptoRandomSource
export const BestDeterminer = new Determiner(BestRandomSource)
